import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import {
  Verified, DataObject, ViewInAr, Link, Gavel,
  Shield, Download, Visibility, Analytics, Description,
  FolderSpecial, ArrowBack
} from '@mui/icons-material';

interface CopyrightBundleLayoutProps {
  children: React.ReactNode;
}

export const CopyrightBundleLayout: React.FC<CopyrightBundleLayoutProps> = ({ children }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const activeRoute = location.pathname;

  const navItems = [
    { route: '/copyright/letter', icon: <Verified fontSize="small" />, label: t('copyright.bundleNav.letter') },
    { route: '/copyright/psd', icon: <DataObject fontSize="small" />, label: t('copyright.bundleNav.psd') },
    { route: '/copyright/watermark', icon: <ViewInAr fontSize="small" />, label: t('copyright.bundleNav.watermark') },
    { route: '/copyright/blockchain', icon: <Link fontSize="small" />, label: t('copyright.bundleNav.blockchain') },
    { route: '/copyright/dispute', icon: <Gavel fontSize="small" />, label: t('copyright.bundleNav.dispute') },
  ];

  const handleDownloadCurrent = () => {
      alert("Downloading current file...");
  };

  const handleDownloadFull = () => {
      alert("Preparing full evidence zip...");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header activeNav="copyright" maxWidth="max-w-6xl" />

      <div className="flex flex-col items-center py-10 px-4">
        <div className="w-full max-w-6xl h-[850px] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-500">
            {/* Header */}
            <header className="h-16 px-6 border-b border-slate-200 flex items-center justify-between flex-shrink-0 bg-white">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/copyright/dashboard')}
                    className="w-8 h-8 hover:bg-slate-100 rounded-lg flex items-center justify-center transition-colors text-slate-400"
                    title={t('copyright.bundleNav.back')}
                >
                    <ArrowBack fontSize="small" />
                </button>
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <FolderSpecial className="text-primary" fontSize="small" />
                </div>
                <h1 className="text-sm font-bold tracking-tight text-slate-700">{t('copyright.bundleNav.details').replace('Bundle Details', 'Bundle #8832-LW Details')}</h1>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={handleDownloadCurrent}
                    className="px-4 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors active:scale-95"
                >
                {t('copyright.bundleNav.downloadCurrent')}
                </button>
                <button
                    onClick={handleDownloadFull}
                    className="px-4 py-2 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-blue-700 transition-all shadow-sm shadow-blue-200 flex items-center gap-2 active:scale-95"
                >
                <Download fontSize="small" style={{ fontSize: 16 }} />
                {t('copyright.bundleNav.downloadAll')}
                </button>
            </div>
            </header>

            <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <aside className="w-[300px] border-r border-slate-200 bg-white flex flex-col flex-shrink-0">
                <div className="p-4 border-b border-slate-200">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.bundleNav.contents')}</span>
                </div>
                <nav className="flex-1 overflow-y-auto py-2">
                {navItems.map((item) => {
                    const isActive = item.route === activeRoute;
                    return (
                    <button
                        key={item.route}
                        onClick={() => navigate(item.route)}
                        className={`w-full flex items-center gap-3 px-6 py-4 transition-all group border-r-2 text-left ${
                        isActive
                            ? 'bg-slate-50 text-primary border-primary'
                            : 'text-slate-500 hover:bg-slate-50 border-transparent'
                        }`}
                    >
                        <span className={isActive ? 'text-primary' : 'text-slate-400'}>{item.icon}</span>
                        <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                    </button>
                    );
                })}
                </nav>
                <div className="p-6 bg-slate-50 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-2 text-slate-900">
                    <Shield fontSize="small" style={{ fontSize: 16 }} />
                    <span className="text-[11px] font-bold uppercase tracking-tight">{t('copyright.bundleNav.securityHash')}</span>
                </div>
                <code className="text-[10px] text-slate-500 break-all font-mono opacity-60 uppercase tracking-tighter leading-tight">SHA-256: 8F4E2D...C9B1A</code>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 bg-white overflow-hidden flex flex-col">
                {/* Sub-header Navigation (Tabs) */}
                <div className="w-full flex justify-center py-6 border-b border-slate-50 bg-white z-10">
                    <div className="flex gap-4">
                        <button
                            onClick={() => navigate('/copyright/letter')}
                            className={`flex items-center gap-2 text-[11px] font-bold px-3 py-1 transition-colors ${activeRoute === '/copyright/letter' ? 'text-primary bg-blue-50 rounded-full border border-blue-100' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <Description style={{ fontSize: 14 }} /> {t('copyright.tabs.docView')}
                        </button>
                        <button
                            onClick={() => navigate('/copyright/psd')}
                            className={`flex items-center gap-2 text-[11px] font-bold px-3 py-1 transition-colors ${activeRoute === '/copyright/psd' ? 'text-primary bg-blue-50 rounded-full border border-blue-100' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <Analytics style={{ fontSize: 14 }} /> {t('copyright.tabs.metaDash')}
                        </button>
                        <button
                            onClick={() => navigate('/copyright/watermark')}
                            className={`flex items-center gap-2 text-[11px] font-bold px-3 py-1 transition-colors ${activeRoute === '/copyright/watermark' ? 'text-primary bg-blue-50 rounded-full border border-blue-100' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <Visibility style={{ fontSize: 14 }} /> {t('copyright.tabs.xray')}
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-12 flex flex-col items-center bg-[#F8FAFC]">
                    {children}
                </div>
            </main>
            </div>
        </div>
      </div>
    </div>
  );
};
